import React, { useState, useEffect, useRef, useCallback } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import {
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaUserCircle,
  FaChevronDown,
  FaMapMarkedAlt,
  FaEnvelope,
} from 'react-icons/fa'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import customTripService from '../services/customTripService'
import logoFallback from '../assets/logo.svg'
import Button from './ui/Button'

/** Pulsing notification badge */
const NotificationBadge = ({ count }) => {
  if (count <= 0) return null
  return (
    <span className="absolute -top-2 -right-5 flex h-4 w-4 items-center justify-center">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
        {count > 9 ? '9+' : count}
      </span>
    </span>
  )
}

/** Desktop auth nav links (bookings, trips/admin, profile dropdown) */
const DesktopAuthNav = ({
  isAdminUser,
  notificationCount,
  navLinkClass,
  user,
  profileOpen,
  setProfileOpen,
  profileRef,
  handleLogout,
}) => (
  <>
    <NavLink to="/bookings" className={navLinkClass}>
      My Bookings
    </NavLink>
    <NavLink to="/my-enquiries" className={navLinkClass}>
      My Enquiries
    </NavLink>
    {isAdminUser ? (
      <NavLink to="/admin" className={navLinkClass}>
        <span className="relative">
          Admin Panel
          <NotificationBadge count={notificationCount} />
        </span>
      </NavLink>
    ) : (
      <NavLink to="/my-custom-trips" className={navLinkClass}>
        <span className="relative">
          My Trips
          <NotificationBadge count={notificationCount} />
        </span>
      </NavLink>
    )}
    <div className="relative ml-2" ref={profileRef}>
      <button
        onClick={() => setProfileOpen(!profileOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 ${
          profileOpen ? 'bg-primary-50 ring-2 ring-primary-400/40' : 'hover:bg-gray-100'
        }`}
      >
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-sm font-semibold shadow-md">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">
          {user?.name}
        </span>
        <FaChevronDown
          className={`text-[10px] text-gray-400 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {profileOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white/90 backdrop-blur-xl shadow-2xl border border-gray-100/80 py-2 ring-1 ring-black/5">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-800">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <Link
            to="/profile"
            onClick={() => setProfileOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors"
          >
            <FaUserCircle className="text-gray-400" />
            My Profile
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <FaSignOutAlt className="text-red-400" />
            Logout
          </button>
        </div>
      )}
    </div>
  </>
)

/** Mobile auth section */
const MobileAuthSection = ({
  isAdminUser,
  notificationCount,
  mobileNavLinkClass,
  closeMobileMenu,
  handleLogout,
}) => (
  <div className="space-y-1">
    <NavLink to="/bookings" className={mobileNavLinkClass} onClick={closeMobileMenu}>
      My Bookings
    </NavLink>
    <NavLink to="/my-enquiries" className={mobileNavLinkClass} onClick={closeMobileMenu}>
      <FaEnvelope className="text-primary-500" />
      My Enquiries
    </NavLink>
    {isAdminUser ? (
      <NavLink to="/admin" className={mobileNavLinkClass} onClick={closeMobileMenu}>
        Admin Panel
        {notificationCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
            {notificationCount} new
          </span>
        )}
      </NavLink>
    ) : (
      <NavLink to="/my-custom-trips" className={mobileNavLinkClass} onClick={closeMobileMenu}>
        My Trips
        {notificationCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
            {notificationCount} new
          </span>
        )}
      </NavLink>
    )}
    <NavLink to="/profile" className={mobileNavLinkClass} onClick={closeMobileMenu}>
      <FaUserCircle className="text-primary-500" />
      My Profile
    </NavLink>
    <button
      onClick={handleLogout}
      className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium text-red-600 hover:bg-red-50 transition-colors"
    >
      <FaSignOutAlt />
      Logout
    </button>
  </div>
)

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [notificationCount, setNotificationCount] = useState(0)
  const [profileOpen, setProfileOpen] = useState(false)
  const { isAuthenticated, user, logout, isAdmin } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const profileRef = useRef(null)
  const menuRef = useRef(null)

  // Detect scroll for glassmorphic effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (!isAuthenticated) return

      try {
        if (isAdmin()) {
          const data = await customTripService.getAdminNotificationCount()
          setNotificationCount(data.count || 0)
        } else {
          const data = await customTripService.getUserNotificationCount()
          setNotificationCount(data.count || 0)
        }
      } catch (error) {
        console.error('Error fetching notification count:', error)
      }
    }

    fetchNotificationCount()
    const interval = setInterval(fetchNotificationCount, 30000)
    return () => clearInterval(interval)
  }, [isAuthenticated, isAdmin])

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleLogout = async () => {
    setProfileOpen(false)
    setIsOpen(false)
    await logout()
    navigate('/login')
  }

  const closeMobileMenu = useCallback(() => setIsOpen(false), [])

  // Nav link styling with animated underline
  const navLinkClass = ({ isActive }) =>
    `relative py-1 text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive
        ? 'text-primary-600 after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-primary-500 after:rounded-full'
        : 'text-gray-600 hover:text-primary-600 after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-primary-400 after:rounded-full after:transition-all after:duration-300 hover:after:w-full'
    }`

  // Mobile nav link styling
  const mobileNavLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
      isActive
        ? 'bg-primary-50 text-primary-600 shadow-sm'
        : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
    }`

  const navItems = [
    { to: '/', label: 'Home' },
    { to: '/tours', label: 'Tours' },
    { to: '/services', label: 'Services' },
    { to: '/about', label: 'About Us' },
    { to: '/contact', label: 'Contact' },
  ]

  return (
    <>
      <nav
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/70 backdrop-blur-2xl shadow-lg border-b border-white/20'
            : 'bg-white/95 backdrop-blur-md shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <Link to="/" className="flex-shrink-0 flex items-center gap-2 group">
              <img
                src={theme.logoUrl || logoFallback}
                alt={theme.companyName}
                className="h-9 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Center Nav Links (desktop) */}
            <div className="hidden lg:flex items-center gap-7">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={navLinkClass} end={item.to === '/'}>
                  {item.label}
                </NavLink>
              ))}

              {/* Tour Planner — accent CTA link */}
              <NavLink
                to="/tour-planner"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-md shadow-primary-500/25'
                      : 'bg-primary-50 text-primary-600 hover:bg-gradient-to-r hover:from-primary-600 hover:to-primary-500 hover:text-white hover:shadow-md hover:shadow-primary-500/25'
                  }`
                }
              >
                <FaMapMarkedAlt className="text-xs" />
                Tour Planner
              </NavLink>
            </div>

            {/* Right Actions (desktop) */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <DesktopAuthNav
                  isAdminUser={isAdmin()}
                  notificationCount={notificationCount}
                  navLinkClass={navLinkClass}
                  user={user}
                  profileOpen={profileOpen}
                  setProfileOpen={setProfileOpen}
                  profileRef={profileRef}
                  handleLogout={handleLogout}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" to="/login">
                    Login
                  </Button>
                  <Button variant="primary" size="sm" to="/register">
                    Sign Up
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden relative z-50 p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay + Drawer */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMobileMenu}
        />

        {/* Drawer Panel */}
        <div
          ref={menuRef}
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white/95 backdrop-blur-2xl shadow-2xl transition-transform duration-300 ease-out ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full pt-20 pb-6 px-5 overflow-y-auto">
            {/* Nav Links */}
            <div className="space-y-1">
              {navItems.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={mobileNavLinkClass}
                  onClick={closeMobileMenu}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Tour Planner — highlighted */}
              <NavLink
                to="/tour-planner"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-md'
                      : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                  }`
                }
                onClick={closeMobileMenu}
              >
                <FaMapMarkedAlt className="text-sm" />
                Tour Planner
              </NavLink>
            </div>

            {/* Auth Section */}
            <div className="mt-auto pt-6 border-t border-gray-200/60">
              {isAuthenticated ? (
                <MobileAuthSection
                  isAdminUser={isAdmin()}
                  notificationCount={notificationCount}
                  mobileNavLinkClass={mobileNavLinkClass}
                  closeMobileMenu={closeMobileMenu}
                  handleLogout={handleLogout}
                />
              ) : (
                <div className="space-y-3 px-4">
                  <Button variant="ghost" fullWidth to="/login" onClick={closeMobileMenu}>
                    Login
                  </Button>
                  <Button variant="primary" fullWidth to="/register" onClick={closeMobileMenu}>
                    Sign Up
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Navbar
